// services/auth.tsx — React context that owns the admin-console
// auth lifecycle.
//
// Status FSM:
//   loading   - SDK initialising / no decision yet. Renders splash.
//   gate      - signed-out. Renders the sign-in card.
//   mfa       - signed in but Firebase needs an SMS code. Renders
//               the MFA form.
//   ready     - signed in + admin allowlist passes. App shell renders.
//   denied    - signed in but not on the allowlist. Sign them out
//               and surface the error.
//
// Lifecycle invariants:
//   - SocialMeshAuth.init was a 600-line IIFE in the marketing repo;
//     this React version implements the same flow natively in TS:
//     Google sign-in (popup + redirect fallback), MFA via
//     PhoneMultiFactorGenerator, admins/{uid} doc allowlist + a
//     hardcoded bootstrap uid for the first-ever admin.
//   - onAuthStateChanged subscribes ONCE per page life. Increments
//     bootCounters.authInit on first install.
//   - Warm-cache fast-path (sessionStorage) survives the rebuild:
//     same key, same 10-min TTL, same race-fix.
//
// Public surface (via useAuth()):
//   { user, status, signIn, signOut, verifyMfaCode, cancelMfa,
//     mfaHint, errorMessage }

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  GoogleAuthProvider,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
  getMultiFactorResolver,
  onAuthStateChanged,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut as fbSignOut,
  type User,
  type MultiFactorResolver,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from './firebase';
import { AuthContext, type AuthContextValue, type AuthStatus } from './authContext';
import { bump } from '@/utils/bootCounters';

// Bootstrap admin uids - required to allow the very first admin to
// sign in before any admins/{uid} doc exists. After bootstrap, the
// allowlist doc takes over.
const BOOTSTRAP_ADMIN_UIDS = ['dh5ehKnLZ6dSnF323LYEmzRmWkk2'];

// Warm cache: sessionStorage key + TTL match the legacy auth.js so
// users tabbing between the React console and any leftover marketing
// page share the same boundary.
const WARM_CACHE_KEY = 'socialmesh-console-auth-cache-v1';
const WARM_CACHE_TTL_MS = 10 * 60 * 1000;

interface WarmCacheEntry {
  uid: string;
  email: string | null;
  ts: number;
}

// ---------------------------------------------------------------------------
// Warm cache helpers
// ---------------------------------------------------------------------------

function readWarmCache(): WarmCacheEntry | null {
  try {
    const raw = window.sessionStorage.getItem(WARM_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as WarmCacheEntry;
    if (!parsed?.uid || typeof parsed.ts !== 'number') return null;
    if (Date.now() - parsed.ts > WARM_CACHE_TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeWarmCache(user: User): void {
  try {
    window.sessionStorage.setItem(
      WARM_CACHE_KEY,
      JSON.stringify({
        uid: user.uid,
        email: user.email,
        ts: Date.now(),
      } satisfies WarmCacheEntry),
    );
  } catch {
    // Quota / private-mode failures are non-fatal.
  }
}

function clearWarmCache(): void {
  try {
    window.sessionStorage.removeItem(WARM_CACHE_KEY);
  } catch {
    // ignore
  }
}

// ---------------------------------------------------------------------------
// Admin allowlist check
// ---------------------------------------------------------------------------

async function isAdmin(user: User): Promise<boolean> {
  if (BOOTSTRAP_ADMIN_UIDS.includes(user.uid)) return true;
  try {
    const snap = await getDoc(doc(db, 'admins', user.uid));
    return snap.exists();
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

// Playwright escape hatch. Set by addInitScript() before page.goto()
// so the layout regression suite can exercise the signed-in shell
// without real Google OAuth in the test browser. Reads as a fake user
// + immediate 'ready' status. Anyone with browser dev-tools can set
// this flag themselves; it's a convenience for tests, not a security
// boundary - the Firestore rules + admin allowlist still gate real
// data access.
function readPlaywrightForceShell(): User | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem('__pw_force_shell');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { uid: string; email: string };
    return {
      uid: parsed.uid,
      email: parsed.email,
      isAnonymous: false,
    } as unknown as User;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const forced = readPlaywrightForceShell();
  const [user, setUser] = useState<User | null>(forced);
  const [status, setStatus] = useState<AuthStatus>(() => {
    if (forced) return 'ready';
    // Fast-path: if we have a fresh warm-cache hit, render the shell
    // immediately. The auth listener still confirms in the background
    // and flips to gate/denied if it disagrees.
    const warm = readWarmCache();
    return warm ? 'ready' : 'loading';
  });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [mfaHint, setMfaHint] = useState<string | null>(null);

  // Held in refs so callbacks don't churn the listener on each render.
  const mfaResolverRef = useRef<MultiFactorResolver | null>(null);
  const mfaVerificationIdRef = useRef<string | null>(null);
  const recaptchaRef = useRef<RecaptchaVerifier | null>(null);
  const authListenerInstalledRef = useRef(false);

  // ---------------------------------------------------------------------------
  // Sign-in (popup with redirect fallback for iOS Safari + Chrome
  // third-party-cookie blocking)
  // ---------------------------------------------------------------------------

  const signIn = useCallback(async (): Promise<void> => {
    setErrorMessage(null);
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    try {
      await signInWithPopup(auth, provider);
    } catch (err) {
      const code = (err as { code?: string })?.code;
      if (
        code === 'auth/popup-blocked' ||
        code === 'auth/cancelled-popup-request' ||
        code === 'auth/popup-closed-by-user'
      ) {
        // Popup blocked / closed - try the redirect path. Note that
        // this navigates away; the auth state restores after the
        // round-trip via getRedirectResult.
        try {
          await signInWithRedirect(auth, provider);
        } catch (redirectErr) {
          setErrorMessage(
            (redirectErr as { message?: string })?.message ??
              'Sign-in failed.',
          );
        }
        return;
      }
      if (code === 'auth/multi-factor-auth-required') {
        await beginMfa(err as Parameters<typeof getMultiFactorResolver>[1]);
        return;
      }
      setErrorMessage(
        (err as { message?: string })?.message ?? 'Sign-in failed.',
      );
    }
  }, []);

  // ---------------------------------------------------------------------------
  // MFA
  // ---------------------------------------------------------------------------

  async function beginMfa(authError: Parameters<typeof getMultiFactorResolver>[1]) {
    try {
      const resolver = getMultiFactorResolver(auth, authError);
      mfaResolverRef.current = resolver;
      const hint = resolver.hints[0];
      if (!hint) {
        setErrorMessage('No MFA hint available. Contact support.');
        return;
      }
      setMfaHint(
        `Enter the verification code sent to ${
          (hint as { phoneNumber?: string }).phoneNumber ?? 'your phone'
        }`,
      );
      // Recaptcha lives on a dedicated div in the MFA gate component.
      // We lazily build it the first time we enter MFA so the
      // typical signed-in user never pays the recaptcha cost.
      if (!recaptchaRef.current) {
        const el = document.getElementById('recaptcha-container');
        if (!el) {
          setErrorMessage(
            'recaptcha-container element missing - MFA cannot proceed.',
          );
          return;
        }
        recaptchaRef.current = new RecaptchaVerifier(auth, el, {
          size: 'invisible',
        });
      }
      const phoneAuthProvider = new PhoneAuthProvider(auth);
      const verificationId = await phoneAuthProvider.verifyPhoneNumber(
        { multiFactorHint: hint, session: resolver.session },
        recaptchaRef.current,
      );
      mfaVerificationIdRef.current = verificationId;
      setStatus('mfa');
    } catch (err) {
      setErrorMessage(
        (err as { message?: string })?.message ?? 'MFA setup failed.',
      );
    }
  }

  const verifyMfaCode = useCallback(async (code: string) => {
    setErrorMessage(null);
    const verificationId = mfaVerificationIdRef.current;
    const resolver = mfaResolverRef.current;
    if (!verificationId || !resolver) {
      setErrorMessage('MFA session expired. Sign in again.');
      setStatus('gate');
      return;
    }
    try {
      const cred = PhoneAuthProvider.credential(verificationId, code);
      const assertion = PhoneMultiFactorGenerator.assertion(cred);
      await resolver.resolveSignIn(assertion);
      // The auth listener picks up the resolved user automatically.
    } catch (err) {
      setErrorMessage(
        (err as { message?: string })?.message ?? 'Code verification failed.',
      );
    }
  }, []);

  const cancelMfa = useCallback(() => {
    mfaResolverRef.current = null;
    mfaVerificationIdRef.current = null;
    setMfaHint(null);
    setStatus('gate');
  }, []);

  // ---------------------------------------------------------------------------
  // Sign-out
  // ---------------------------------------------------------------------------

  const handleSignOut = useCallback(async () => {
    clearWarmCache();
    await fbSignOut(auth);
    // Auth listener flips us to 'gate'.
  }, []);

  // ---------------------------------------------------------------------------
  // Listener install (once per tab)
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (authListenerInstalledRef.current) return;
    authListenerInstalledRef.current = true;
    bump('authInit');
    // Playwright force-shell path: skip the real listener entirely.
    if (forced) return;

    let redirectChecked = false;
    let firstAuthStateFired = false;

    const decide = async (firebaseUser: User | null) => {
      if (firebaseUser) {
        const allowed = await isAdmin(firebaseUser);
        if (allowed) {
          writeWarmCache(firebaseUser);
          setUser(firebaseUser);
          setStatus('ready');
          setErrorMessage(null);
        } else {
          clearWarmCache();
          setUser(null);
          setStatus('denied');
          setErrorMessage(
            'Access denied. This account is not on the admin allowlist.',
          );
          await fbSignOut(auth);
        }
      } else if (redirectChecked) {
        clearWarmCache();
        setUser(null);
        setStatus('gate');
      }
    };

    const unsub = onAuthStateChanged(auth, (u) => {
      firstAuthStateFired = true;
      decide(u);
    });

    getRedirectResult(auth)
      .then(() => {
        redirectChecked = true;
        if (firstAuthStateFired && !auth.currentUser) {
          // Listener already fired with null and we deferred showing
          // the gate; now both signals agree.
          decide(null);
        }
      })
      .catch((err) => {
        redirectChecked = true;
        const code = (err as { code?: string })?.code;
        if (code === 'auth/multi-factor-auth-required') {
          beginMfa(err);
        } else if (firstAuthStateFired && !auth.currentUser) {
          decide(null);
        }
      });

    return unsub;
    // The listener installs exactly once per tab life. `forced` is
    // read synchronously from sessionStorage before the effect runs
    // and never flips for the component's lifetime, so leaving it
    // out of the dep array is intentional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      status,
      errorMessage,
      mfaHint,
      signIn,
      signOut: handleSignOut,
      verifyMfaCode,
      cancelMfa,
    }),
    [
      user,
      status,
      errorMessage,
      mfaHint,
      signIn,
      handleSignOut,
      verifyMfaCode,
      cancelMfa,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
